// Shared double-submit guard.
//
// Wrap an async action so its trigger button can't fire the action again while
// the first run is still in flight. A rapid double-click would otherwise fire
// the handler twice before the first DB write lands, creating duplicate records
// (duplicate sales, invites, expenses, etc.) or firing duplicate side effects.
//
// Re-entry is blocked via a data flag set on the element itself, so it holds
// even when the same button is wired through more than one listener. The button
// is also disabled (and optionally shows busy text) until the promise settles,
// then fully restored to its previous state.
//
// Usage:
//   btn.addEventListener('click', () => withSubmitGuard(btn, () => handler(data), { busyText: 'Saving…' }));
//
// Pass `busyText` only for plain-text buttons. Omit it for buttons whose label
// contains markup (e.g. an SVG icon) - setting textContent would wipe the icon;
// the disable + re-entry flag still protect those.
//
// @param {HTMLElement|null} btn        the element that triggered the action
// @param {() => any} asyncFn           the work to run once (may be async)
// @param {{ busyText?: string }} [opts]
// @returns the asyncFn result, or undefined if a call was already in flight
export async function withSubmitGuard(btn, asyncFn, { busyText } = {}) {
  // No element to guard against - just run the work.
  if (!btn) return asyncFn();
  // A run is already in flight for this button - swallow the extra click.
  if (btn.dataset.submitting === '1') return;

  btn.dataset.submitting = '1';
  const prevText = btn.textContent;
  const wasDisabled = btn.disabled;
  btn.disabled = true;
  if (busyText != null) btn.textContent = busyText;

  try {
    return await asyncFn();
  } finally {
    delete btn.dataset.submitting;
    btn.disabled = wasDisabled;
    if (busyText != null) btn.textContent = prevText;
  }
}
